import { setPageMeta } from '../seo'
import React, { useEffect } from 'react'
import SiteFooter from '../components/SiteFooter'
import SiteMark from '../components/SiteMark'
import PageHeader from '../components/PageHeader'
import PressCarousel from '../components/PressCarousel'
import ContactForm from '../components/ContactForm'

// Hillary's own page. She is the subject of both films and neither film's page
// is the right place for her. The words are Elaine's, used as written.

const expeditions = [
  {
    label: 'High Arctic, July 2025',
    value: 'Performed on moving pack ice at 82\u00b0 North to draw attention to vanishing sea ice.',
  },
  {
    label: 'Bhutan, October 2023',
    value: 'Invited to the Royal Highland Festival in Laya, four thousand metres above sea level, to perform for His Majesty the King. She made history as the first foreigner and the youngest artist ever invited to perform since the festival\u2019s inception in 2016.',
  },
  {
    label: 'Antarctica, February 2023',
    value: 'Served as the youngest climate ambassador on the Antarctic Climate Expedition, performing at Neko Harbour.',
  },
]


const Hillary = () => {
  useEffect(() => {
    setPageMeta({
      title: 'Hillary O\u2019Sullivan | Pianist and climate youth ambassador',
      path: '/hillary-osullivan',
      description: 'Hillary O\u2019Sullivan is a Singaporean classical pianist and climate youth ambassador. She has performed at 82\u00b0 North on Arctic pack ice, at four thousand metres in Bhutan, and in Antarctica.',
    })
    window.scrollTo(0, 0)
  }, [])

  return (
    <div className='App page-standard page-hillary'>
      <SiteMark />
      <PageHeader
        image='/assets/hillary/hillary-osullivan-arctic-pack-ice-82n.jpg'
        focus='50% 100%'
        focusMobile='49% 100%'
        alt='Hillary O&rsquo;Sullivan playing a grand piano on Arctic pack ice at 82 degrees north'
        title='Hillary'
        srTitle='Hillary O&rsquo;Sullivan, pianist and climate youth ambassador'
        subtitle='pianist'
      />

      <main>
        <section className='hillary-hook' data-tone='light'>
          <div className='content-container'>
            <p>
              Hillary O&rsquo;Sullivan is a Singaporean classical pianist and
              climate youth ambassador who believes that music can carry an
              emotional argument where cold data falls short.
            </p>
          </div>
        </section>

        <section className='hillary-mission' data-tone='dark'>
          <div className='content-container'>
            <h2>The mission</h2>
            <p>
              While climate change is frequently debated through abstract
              numbers, most people struggle to connect with statistics alone.
              Hillary takes her piano directly to the environments under threat,
              performs on location, and allows the audience to truly feel the
              stakes.
            </p>
            <p>
              Working alongside established field scientists and local
              conservation organisations, her performances break away from the
              traditional concert hall. Beginning her training at age three,
              Hillary realised by twelve that transporting a piano into
              challenging, remote terrains speaks far louder than a conventional
              speech.
            </p>
          </div>
        </section>

        <section className='hillary-proof' data-tone='light'>
          <div className='content-container'>
            <h2>Expedition highlights</h2>
            <dl className='hillary-milestones'>
              {expeditions.map((m) => (
                <div className='hillary-milestone' key={m.label}>
                  <dt>{m.label}</dt>
                  <dd>{m.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        <section className='hillary-films' data-tone='dark'>
          <div className='content-container'>
            <h2>Films</h2>
            <ul className='hillary-film-list'>
              <li>
                <span className='hillary-film-year'>In pre-production</span>
                <h3><a href='/handslikeours'>Hands Like Ours</a></h3>
                <p>
                  Documenting the journey into North Sumatra&rsquo;s Leuser
                  Ecosystem, the last shared habitat of Sumatra&rsquo;s
                  megafauna. Filming in 2027.
                </p>
              </li>
              <li>
                <span className='hillary-film-year'>2024</span>
                <h3><a href='/'>The Girl and Her Piano</a></h3>
                <p>
                  A short documentary capturing her journey to the high-altitude
                  glaciers of Bhutan. Broadcast on PBS, screened at
                  international festivals across four countries, and adopted as
                  educational material in Colorado schools.
                </p>
              </li>
            </ul>
          </div>
        </section>

        <section className='hillary-music' data-tone='light'>
          <div className='content-container hillary-split'>
            <figure className='hillary-split-image'>
              <img
                src='/assets/hillary/hillary-osullivan-at-the-piano.jpg'
                alt='Hillary O&rsquo;Sullivan at the piano'
                width='675'
                height='900'
                loading='lazy'
              />
            </figure>
            <div className='hillary-split-text'>
              <h2>Musical background</h2>
              <p>
                Classically trained from the age of three, Hillary holds an
                ABRSM Grade 8 certification and is currently preparing for her
                ARSM Performance Diploma. She has performed at the Chopin
                Festival in Poland, the International Festival of Music in
                Granada, and the Esplanade in Singapore, including a performance
                for the President of Singapore.
              </p>
              <p>
                She is a prize-winner at the Singapore Raffles Music Competition,
                received the Brilliant Award at the Brilliant Talent Discovery
                Awards for her interpretation of Debussy, and in September 2026
                received Platinum and First Prize at the Quatre Music
                International Festival.
              </p>
              <p>
                Her signature performance piece, <em>Rhapsody for a Changing
                Antarctic</em>, was specifically composed for her expedition by
                Belgian composer Eric Bettens to capture the sound of an
                ecosystem coming apart. She has since carried this powerful
                composition to the Himalayan heights and across the Arctic sea
                ice.
              </p>
            </div>
          </div>
        </section>

        <section className='press-block' data-tone='light'>
          <div className='content-container'>
            <h2>Selected press</h2>
          </div>
          <PressCarousel />
        </section>

        <section className='hillary-contact' data-tone='light'>
          <div className='content-container'>
            <span className='hillary-rule' aria-hidden='true' />
            <h2>Contact</h2>
            <p className='hillary-contact-intro'>
              For interviews, performances and school visits.
            </p>
            <div className='hillary-form'>
              <ContactForm subjectHint='Hillary O&rsquo;Sullivan page' />
            </div>
            <p className='hillary-contact-links'>
              <a href='mailto:info@thegirlandherpiano.com'>Email</a>
              <a href='https://www.instagram.com/thegirlandherpiano' target='_blank' rel='noopener noreferrer'>Instagram</a>
              <a href='https://www.facebook.com/thegirlandherpiano' target='_blank' rel='noopener noreferrer'>Facebook</a>
              <a href='https://www.youtube.com/@hillaryosullivan' target='_blank' rel='noopener noreferrer'>YouTube</a>
            </p>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  )
}

export default Hillary
